import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Device } from '@capacitor/device';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import addIcon from "../../assets/images/add-icon.svg";
import user from "../../assets/images/menu/account.svg";
import Checkbox from '../../components/layouts/CheckBox';
import Logout from '../../components/layouts/Logout';
import Button from '../../components/ui/Button';
import CustomInput from '../../components/ui/CustomInput';
import CustomPhoneInput from '../../components/ui/CustomPhoneInput';
import CustomReactSelect from '../../components/ui/CustomReactSelect';
import Skeleton from '../../components/ui/Skeleton';
import { ApiConstants } from '../../constants/ApiConstants';
import { ApiTypes } from '../../constants/Constants';
import { Routes } from '../../constants/RouteConstants';
import authContext from '../../context/authContext';
import useMakeRequest from '../../utils/ApiHelper';
import { handleLogout, validImageTypes, validateImage } from '../../utils/Helper';
import ProfileValidation from '../../validations/auth/ProfileValidation';
import DoNotSellMyPersonalInfoModal from '../cms-public/DoNotSellMyPersonalInfoModal';

interface profileInputs {
    name: string;
    email: string;
    phone_country_code: string,
    phone_number: string,
    country: string,
    state: string,
    income: string,
    profile_pic: any
    locality: string;
    monthly_work_hour: string,
    receive_marketing_emails?: boolean;
    show_income_to_other_hh?: boolean;
    share_usage_data?: boolean;
};

const ManageProfile = () => {
    const contentRef = useRef<HTMLDivElement | null>(null);

    const [profileImage, setProfileImage]: any = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const localities = useMakeRequest();
    const profile: any = useContext(authContext);
    console.log("profile: ", profile)
    const update_profile = useMakeRequest();
    const countries = useMakeRequest();
    const states = useMakeRequest();
    const cmsPage = useMakeRequest();

    const [isIos, setIsIos] = useState(false);

    const { register, watch, setValue, formState: { errors }, handleSubmit, trigger, getValues } = useForm<profileInputs>({
        mode: 'onChange', resolver: yupResolver(ProfileValidation) as any
    });

    const handleProImg = (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
            const validationError = validateImage(file, validImageTypes);
            if (validationError !== true) {
                toast.error(validationError as string);
                return;
            }
            const imageURL = URL.createObjectURL(file);
            setProfileImage(imageURL);
            setValue("profile_pic", file);
            trigger('profile_pic')
            $('.btn-close-select-photo').click()
        }
    };

    const handleMobileNumber = (value: any, code: any) => {
        const regex = new RegExp(code?.dialCode);
        const phone = value.replace(regex, "");
        setValue('phone_country_code', code?.dialCode)
        setValue('phone_number', phone)
        trigger("phone_number")
    }

    useEffect(() => {
        if (profile?.data) {
            if (profile?.data?.is_profile_setup === 1) {
                navigate(Routes?.Dashboard)
            } else {
                setValue('name', profile?.data.name);
                setValue('email', profile?.data.email);
            }
        }
    }, [profile?.data])



    const onSubmit = (data: any) => {
        const payload = {
            ...data,
            income: data?.income ? data.income.replace(/,/g, '') : '',
            locality: data?.locality?.value,
            profile_pic: watch('profile_pic')
        }
        toast.promise(
            update_profile.makeRequest({ url: ApiConstants?.UpdateProfile, payload, type: ApiTypes?.FORM_DATA }),
            {
                loading: 'Processing...',
                success: (data: any) => data.message,
                error: (error: any) => error.message,
            }
        );
    }

    useEffect(() => {
        if (update_profile?.status) {
            const fetch = async () => {
                await profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET })
                navigate(Routes?.Subscription, { state: { is_first_time: true } })
            }
            fetch()
        }
    }, [update_profile?.status])


    useEffect(() => {
        countries.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: 1, showAll: true } })

        const checkDeviceType = async () => {
            const info = await Device.getInfo()
            if (info?.platform === 'ios') {
                setIsIos(true)
            }
        }
        checkDeviceType()
    }, [])

    useEffect(() => {
        if (countries?.status) {
            setValue('country', countries?.data?.data[0]?.value)
        }
    }, [countries?.status])

    useEffect(() => {
        if (watch('country')) {
            states.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: 2, showAll: true, list_id: watch('country') } })
        }
    }, [watch('country')])

    useEffect(() => {
        if (watch('state')) {
            localities.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: 3, showAll: true, list_id: watch('state') } })
        }
    }, [watch('state')])

    const handleOpenCamera = async () => {
        const permissionResult = await Camera.checkPermissions();
        if (permissionResult?.camera === 'granted') {
            const photo = await Camera.getPhoto({
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                quality: 60,
            });
            if (photo?.webPath) {
                const response = await fetch(photo.webPath);
                const blob = await response.blob();
                const file = new File([blob], `profile_pic_${Date.now()}.${photo?.format}`, { type: blob.type });
                setValue('profile_pic', file)
                setProfileImage(photo?.webPath);
                $('.btn-close-select-photo').click()
            }
        } else if (permissionResult?.camera === 'denied') {
            toast.error('Camera permission denied. Please enable it in settings to use this feature.');
        } else {
            const photo = await Camera.getPhoto({
                // allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                quality: 100,
            });
            if (photo?.webPath) {
                const response = await fetch(photo.webPath);
                const blob = await response.blob();
                const file = new File([blob], `profile_pic_${Date.now()}.${photo?.format}`, { type: blob.type });
                setValue('profile_pic', file)
                setProfileImage(photo?.webPath);
                $('.btn-close-select-photo').click()
            }
        }
    };

    const handleOpenGallery = async () => {
        const permissionResult = await Camera.checkPermissions();
        if (permissionResult?.photos === 'granted') {
            const image = await Camera.getPhoto({
                resultType: CameraResultType.Uri,
                source: CameraSource.Photos,
                allowEditing: false,
                quality: 60
            });
            if (image?.webPath) {
                const response = await fetch(image.webPath);
                const blob = await response.blob();
                const file = new File([blob], `profile_pic_${Date.now()}.${image?.format}`, { type: blob.type });
                setValue('profile_pic', file)
                setProfileImage(image?.webPath);
                $('.btn-close-select-photo').click()
            }
        } else if (permissionResult?.camera === 'denied') {
            toast.error('Photo Library permission denied. Please enable it in settings to use this feature.');
        } else {
            const image = await Camera.getPhoto({
                resultType: CameraResultType.Uri,
                source: CameraSource.Photos,
                allowEditing: false,
                quality: 60
            });
            if (image?.webPath) {
                const response = await fetch(image.webPath);
                const blob = await response.blob();
                const file = new File([blob], `profile_pic_${Date.now()}.${image?.format}`, { type: blob.type });
                setValue('profile_pic', file)
                setProfileImage(image?.webPath);
                $('.btn-close-select-photo').click()
            }
        }
    };

    const viewCmsPage = (slug: string) => {
        cmsPage.makeRequest({ payload: { detail_id: slug }, url: ApiConstants?.CmsPagesDetail })
    }

    useEffect(() => {
        const handleContentClick = (event: { target: any; preventDefault: () => void; }) => {
            const clickedElement = event.target;

            console.log("clickedElement: ", clickedElement);

            // Identify the link: "Do not share my personal info"
            // Still recommend using a more robust identifier (ID/class from CMS) if possible.
            if (clickedElement.textContent.includes('Do not share my personal info')) {
                event.preventDefault(); // Prevent default link behavior

                // Get a reference to the first modal element
                const firstModalElement = $('#exampleModal'); // jQuery selector

                // 1. Trigger the close button click on the first modal
                firstModalElement.find('.btn-close').click();

                // 2. Listen for the 'hidden.bs.modal' event on the first modal
                // This ensures the first modal is fully closed before opening the second.
                firstModalElement.one('hidden.bs.modal', function () {
                    // 3. Show the second modal
                    // @ts-ignore
                    $('#personalLinkModal').modal('show');
                });
            }
        };

        const currentContentRef = contentRef.current;
        if (currentContentRef) {
            // Attach the event listener to the modal-body div
            currentContentRef.addEventListener('click', handleContentClick);
        }

        // Cleanup: remove the event listener when the component unmounts
        return () => {
            if (currentContentRef) {
                currentContentRef.removeEventListener('click', handleContentClick);
            }
        };
    }, [cmsPage]);

    const hasProfilePic = profileImage

    return (
        <div className="auth-page manage-profile-page">
            <header role="banner" className="profile-header">
                <h1 className='auth-title mb-0'>Manage Profile</h1>
                <div
                    role="button"
                    tabIndex={0}
                    className='logout-auth'
                    onClick={() => setIsModalOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            setIsModalOpen(true);
                        }
                    }}
                >
                    Log out
                </div>
            </header>
            <main id="main-content" className="auth-container">
                <form onSubmit={handleSubmit(onSubmit)} className='auth-form-wrap'>
                    <div className="auth-form">
                        <div className="auth-form-fields">
                            <div className='profile-img-area'>
                                <div className='profile-wrap-border'>
                                    <div className='profile-inner-img'>
                                        {hasProfilePic ? (
                                            <Zoom>
                                                <img className="profile-img" src={profileImage} alt="" />
                                            </Zoom>
                                        ) : (
                                            <img src={user} alt="photo" className="dummy-profile" />
                                        )}
                                    </div>
                                </div>

                                <label className='add-photo-btn' data-bs-toggle="offcanvas" data-bs-target="#photoSelect">
                                    <img
                                        src={addIcon}
                                        alt={'photo'}
                                        className="add-icon-img"
                                    /> Add Photo
                                </label>

                                <input hidden id="openCameraOptions" type="file" accept="image/*" />
                                <input hidden id="openGallery" type="file" onChange={handleProImg} accept="image/*" />
                                <input hidden id="openCamera" type="file" onChange={handleProImg} accept="image/*" capture="environment" />
                                <ErrorMessage errors={errors} name="profile_pic" render={({ message }) => <p className='valid-error'>{message}</p>} />

                            </div>

                            <CustomInput
                                label="Name"
                                type="text"
                                placeholder="Enter name"
                                required
                                {...register('name')}
                                error={errors.name?.message}
                            />

                            <CustomInput
                                label="Email"
                                type="email"
                                placeholder="Enter email"
                                required
                                disabled
                                {...register('email')}
                                error={errors.email?.message}
                            />

                            <CustomPhoneInput
                                label="Mobile Number"
                                value={`${watch('phone_country_code') || ''}${watch('phone_number') || ''}`}
                                onChange={handleMobileNumber}
                                error={errors.phone_number?.message}
                                defaultCountry="us"
                                placeholder="+1 (555) 000-0000"
                            />

                            <CustomReactSelect
                                label="Country"
                                required
                                options={countries?.data?.data}
                                value={{
                                    value: countries?.data?.data?.[0]?.value,
                                    label: countries?.data?.data?.[0]?.label
                                }}
                                placeholder="Please select household name"
                                onChange={(selectedOption: any) => {
                                    setValue("country", selectedOption ? selectedOption?.value : "");
                                    trigger("country");
                                }}
                                isLoading={countries?.loading}
                                error={errors}
                                errorName="country"
                                closeMenuOnScroll={true}
                                closeMenuOnSelect={true}

                            />


                            <CustomReactSelect
                                label="State"
                                required
                                options={states?.data?.data}
                                // value={{
                                //     value: states?.data?.data?.[0]?.value,
                                //     label: states?.data?.data?.[0]?.label
                                // }}
                                placeholder="Select state"
                                onChange={(selectedOption: any) => {
                                    setValue("state", selectedOption ? selectedOption?.value : "");
                                    setValue('locality', "")
                                    trigger("state")
                                }}
                                isLoading={states?.loading}
                                error={errors}
                                errorName="state"
                                closeMenuOnScroll={true}
                                closeMenuOnSelect={true}
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />

                            <CustomReactSelect
                                label="Locality"
                                required
                                options={localities?.data?.data}
                                defaultValue={profile?.data?.locality_detail}
                                value={watch('locality') && watch('locality')}
                                placeholder="Select locality"
                                onChange={(selectedOption: any) => {
                                    setValue("locality", selectedOption ? selectedOption : "");
                                    trigger("locality")
                                }}
                                isLoading={localities?.loading}
                                closeMenuOnScroll={true}
                                closeMenuOnSelect={true}
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                                error={errors}
                                errorName="locality"
                            />



                            <CustomInput
                                label="Income"
                                placeholder='$00.00'
                                tooltip={{
                                    id: "my-income",
                                    content: "Income is only required for households using the income-adjusted split. If you enter your income on this page, other household members may be able to estimate your income.",
                                    place: "bottom"
                                }}
                                {...register('income')}
                                onInput={(e: any) => {
                                    let value = e.target.value;

                                    // remove commas first
                                    value = value.replace(/,/g, '');

                                    // allow only numbers and decimal
                                    value = value.replace(/[^0-9.]/g, '');

                                    // allow only one decimal
                                    const decimalCount = value.split('.').length - 1;
                                    if (decimalCount > 1) {
                                        const parts = value.split('.');
                                        value = parts[0] + '.' + parts.slice(1).join('');
                                    }

                                    let parts = value.split('.');

                                    // limit integer length
                                    if (parts[0].length > 7) {
                                        parts[0] = parts[0].substring(0, 7);
                                    }

                                    // limit decimal to 2
                                    if (parts[1]) {
                                        parts[1] = parts[1].substring(0, 2);
                                    }

                                    // add commas to integer part
                                    parts[0] = Number(parts[0]).toLocaleString('en-US');

                                    e.target.value = parts.join('.');
                                }}
                                error={errors.income?.message}
                            />

                            <Checkbox
                                register={register}
                                labelText=""
                                id='show_income_to_other_hh'
                                name='show_income_to_other_hh'
                                customLabelClassName="profile-check-box-label"
                                links={[
                                    { text: 'Show income to other household members?', isLink: false }
                                ]}
                            />

                            <CustomInput
                                label="Hours of Employment (Monthly)"
                                placeholder="Enter hours"
                                tooltip={{
                                    id: "hours-of-employment",
                                    content: "This information is optional and will be visible to other household members. Please record the average number of hours actually worked in a month, omitting paid time off and any form of leave.",
                                    place: "bottom"
                                }}
                                {...register('monthly_work_hour')}
                                onInput={(e: any) => {
                                    let value = e.target.value;

                                    // Remove non-digits
                                    value = value.replace(/\D/g, "");

                                    // Prevent multiple leading zeros
                                    if (value === "00" || value === "000") {
                                        value = "0";
                                    }

                                    // If starts with 0 and has more digits, remove leading zero
                                    if (value.length > 1 && value.startsWith("0")) {
                                        value = value.replace(/^0+/, "");
                                    }

                                    // Limit to 3 digits
                                    if (value.length > 3) {
                                        value = value.slice(0, 3);
                                    }

                                    // Cap max value to 720
                                    if (Number(value) > 720) {
                                        value = "720";
                                    }

                                    e.target.value = value;
                                }}
                                error={errors.monthly_work_hour?.message}
                            />

                            <Checkbox
                                register={register}
                                labelText=""
                                id='receive_marketing_emails'
                                name='receive_marketing_emails'
                                customLabelClassName="profile-check-box-label"
                                links={[
                                    { text: 'I agree to receive notifications and emails about exclusive promotions, marketing offers, and other updates.', isLink: false }
                                ]}
                            />
                            <Checkbox
                                register={register}
                                name='share_usage_data'
                                id='share_usage_data'
                                customLabelClassName="profile-check-box-label"
                                labelText="Yes! I agree to advance household fairness by sharing usage and analytics data with Justivide. All data sharing is subject to the"
                                links={[
                                    { text: 'Privacy Policy.', isLink: true, onClick: () => viewCmsPage('privacy-policy') },
                                ]}
                            />

                            <ErrorMessage
                                errors={errors}
                                name="share_usage_data"
                                render={({ message }) => <p className='valid-error'>{message}</p>}
                            />
                        </div>

                        <div className="auth-form-actions mb-5">
                            <Button
                                className='com-btn mt-3'
                                type="submit"
                                loading={update_profile?.loading}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                </form>

                {/*==== IMAGE SELECT DRAWER START ====*/}
                <div
                    className="offcanvas offcanvas-bottom inner-modal"
                    tabIndex={-1}
                    id="photoSelect"
                    data-bs-backdrop="static"
                    data-bs-keyboard="false"
                    data-bs-scroll="false"
                    aria-labelledby="photoSelectLabel"
                    style={{ backgroundColor: '#fff', minHeight: '20%', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}
                >
                    <div className="offcanvas-body d-flex justify-content-center align-items-center flex-column">
                        {/* <label className="d-flex align-items-center justify-content-center w-100 btn btn-light my-2 py-3" htmlFor='openGallery'>
                        <i className="fa fa-camera me-3" aria-hidden="true"></i> Upload from Photos
                    </label>
                    {isMobile ? <label className="d-flex align-items-center justify-content-center w-100 btn btn-light mb-2 py-3" htmlFor='openCamera'>
                        <i className="fa fa-picture-o me-3" aria-hidden="true"></i> Take a Photo
                    </label> : ''}
                    <label className="d-flex align-items-center justify-content-center w-100 btn btn-secondary py-3 btn-close-select-photo" data-bs-dismiss="offcanvas" aria-label="Close">
                        Cancel
                    </label> */}

                        <label className="d-flex align-items-center justify-content-center w-100 btn btn-light my-2 py-3" onClick={handleOpenGallery}>
                            <i className="fa fa-camera me-3" aria-hidden="true"></i> Upload from Photos
                        </label>
                        {isMobile ? <label className="d-flex align-items-center justify-content-center w-100 btn btn-light mb-2 py-3" onClick={handleOpenCamera}>
                            <i className="fa fa-picture-o me-3" aria-hidden="true"></i> Take a Photo
                        </label> : ''}
                        <label className="d-flex align-items-center justify-content-center w-100 btn btn-secondary py-3 btn-close-select-photo" data-bs-dismiss="offcanvas" aria-label="Close">
                            Cancel
                        </label>

                    </div>
                </div>
                {/*==== IMAGE SELECT DRAWER END ====*/}

                {isModalOpen &&
                    <Logout handleLogout={() => handleLogout(navigate)} toggleModal={() => setIsModalOpen(false)} />
                }
                <div ref={contentRef} className="modal fade inner-modal c" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title fs-5" id="exampleModalLabel">{cmsPage?.data?.data?.title}</h1>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                {
                                    cmsPage?.loading ?
                                        <>
                                            <Skeleton className='skeleton-loader mt-5' style={{ width: '50%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '60%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '100%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '80%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '90%', height: '20px' }} />

                                            <Skeleton className='skeleton-loader mt-5' style={{ width: '50%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '60%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '100%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '80%', height: '20px' }} />
                                            <Skeleton className='skeleton-loader my-2' style={{ width: '90%', height: '20px' }} />
                                        </>
                                        :
                                        <>
                                            <p dangerouslySetInnerHTML={{ __html: cmsPage?.data?.data?.content }}></p>
                                            {/* <div className="mb-3 com-input-grp">
                                            <label className="form-label">6. Do Not Sell My Personal Information</label>
                                        </div> */}
                                        </>
                                }            </div>
                        </div>
                    </div>
                </div>
                <DoNotSellMyPersonalInfoModal cmsPage={cmsPage} />
            </main>
        </div>
    )
}

export default ManageProfile